import React, { Component } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCopy, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { compareTwoSources } from "../../services/marketDataService";
import "./MarketDataComparison.css";

class MarketDataComparison extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Source 1 inputs
      source1Host: "192.168.29.113",
      source1Username: "",
      source1Password: "",
      source1Date: "2025-12-02",
      source1Time: "09:17:00",
      source1TradeType: "live",
      
      // Source 2 inputs
      source2Host: "192.168.29.113",
      source2Username: "",
      source2Password: "",
      source2Date: "2025-12-02",
      source2Time: "09:17:00",
      source2TradeType: "paper",
      
      // Optional symbol
      symbol: "",
      
      // Results
      comparisonResult: null,
      error: null,
      isLoading: false,
      
      // Trade types
      tradeTypes: [
        { value: "live", label: "Live" },
        { value: "paper", label: "Paper" },
      ],
    };
  }

  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      error: null,
      comparisonResult: null,
    });
  };

  handleCompare = async () => {
    const {
      source1Host,
      source1Username,
      source1Password,
      source1Date,
      source1Time,
      source1TradeType,
      source2Host,
      source2Username,
      source2Password,
      source2Date,
      source2Time,
      source2TradeType,
      symbol,
    } = this.state;

    // Validation
    if (!source1Host || !source1Date || !source1Time || !source1TradeType) {
      this.setState({ error: "Please fill all Source 1 fields" });
      return;
    }

    if (!source2Host || !source2Date || !source2Time || !source2TradeType) {
      this.setState({ error: "Please fill all Source 2 fields" });
      return;
    }

    this.setState({ isLoading: true, error: null, comparisonResult: null });

    try {
      const result = await compareTwoSources({
        source1Host,
        source1Username: source1Username || null,
        source1Password: source1Password || null,
        source1Date,
        source1Time,
        source1TradeType,
        source2Host,
        source2Username: source2Username || null,
        source2Password: source2Password || null,
        source2Date,
        source2Time,
        source2TradeType,
        symbol: symbol || null,
      });

      this.setState({
        comparisonResult: result,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to compare records. Please check your inputs and try again.";
      this.setState({
        error: errorMessage,
        isLoading: false,
        comparisonResult: null,
      });
    }
  };

  copyToClipboard = () => {
    if (this.state.comparisonResult) {
      const jsonString = JSON.stringify(this.state.comparisonResult, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        alert("JSON copied to clipboard!");
      });
    }
  };

  formatJsonValue = (value) => {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  render() {
    const {
      source1Host,
      source1Username,
      source1Password,
      source1Date,
      source1Time,
      source1TradeType,
      source2Host,
      source2Username,
      source2Password,
      source2Date,
      source2Time,
      source2TradeType,
      symbol,
      comparisonResult,
      error,
      isLoading,
      tradeTypes,
    } = this.state;

    return (
      <div className="market-data-comparison">
        <Card>
          <Card.Header>
            <h4>MongoDB Market Data Comparison</h4>
            <p className="text-muted mb-0">
              Compare MarketDataPOJO records from two different MongoDB sources
            </p>
          </Card.Header>
          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => this.setState({ error: null })}>
                {error}
              </Alert>
            )}

            <Row>
              {/* Source 1 */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-primary text-white">
                    <strong>Source S1</strong>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>MongoDB Host</Form.Label>
                      <FormControl
                        type="text"
                        value={source1Host}
                        onChange={(e) =>
                          this.handleInputChange("source1Host", e.target.value)
                        }
                        placeholder="e.g., 192.168.29.113"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Username (Optional)</Form.Label>
                      <FormControl
                        type="text"
                        value={source1Username}
                        onChange={(e) =>
                          this.handleInputChange("source1Username", e.target.value)
                        }
                        placeholder="Leave empty if no auth"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Password (Optional)</Form.Label>
                      <FormControl
                        type="password"
                        value={source1Password}
                        onChange={(e) =>
                          this.handleInputChange("source1Password", e.target.value)
                        }
                        placeholder="Leave empty if no auth"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <FormControl
                        type="date"
                        value={source1Date}
                        onChange={(e) =>
                          this.handleInputChange("source1Date", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Time</Form.Label>
                      <FormControl
                        type="time"
                        value={source1Time}
                        onChange={(e) =>
                          this.handleInputChange("source1Time", e.target.value)
                        }
                        step="1"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Trade Type</Form.Label>
                      <FormControl
                        as="select"
                        value={source1TradeType}
                        onChange={(e) =>
                          this.handleInputChange("source1TradeType", e.target.value)
                        }
                      >
                        {tradeTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </FormControl>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Source 2 */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-success text-white">
                    <strong>Source S2</strong>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>MongoDB Host</Form.Label>
                      <FormControl
                        type="text"
                        value={source2Host}
                        onChange={(e) =>
                          this.handleInputChange("source2Host", e.target.value)
                        }
                        placeholder="e.g., 192.168.29.113"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Username (Optional)</Form.Label>
                      <FormControl
                        type="text"
                        value={source2Username}
                        onChange={(e) =>
                          this.handleInputChange("source2Username", e.target.value)
                        }
                        placeholder="Leave empty if no auth"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Password (Optional)</Form.Label>
                      <FormControl
                        type="password"
                        value={source2Password}
                        onChange={(e) =>
                          this.handleInputChange("source2Password", e.target.value)
                        }
                        placeholder="Leave empty if no auth"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <FormControl
                        type="date"
                        value={source2Date}
                        onChange={(e) =>
                          this.handleInputChange("source2Date", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Time</Form.Label>
                      <FormControl
                        type="time"
                        value={source2Time}
                        onChange={(e) =>
                          this.handleInputChange("source2Time", e.target.value)
                        }
                        step="1"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Trade Type</Form.Label>
                      <FormControl
                        as="select"
                        value={source2TradeType}
                        onChange={(e) =>
                          this.handleInputChange("source2TradeType", e.target.value)
                        }
                      >
                        {tradeTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </FormControl>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Optional Symbol */}
            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Symbol (Optional)</Form.Label>
                  <FormControl
                    type="text"
                    value={symbol}
                    onChange={(e) =>
                      this.handleInputChange("symbol", e.target.value)
                    }
                    placeholder="e.g., NIFTY 50"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Compare Button */}
            <Row>
              <Col md={12} className="text-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={this.handleCompare}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Comparing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="mr-2" />
                      Compare
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {/* Results */}
            {comparisonResult && (
              <Row className="mt-4">
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>Comparison Results - Differences Only</strong>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={this.copyToClipboard}
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1" />
                        Copy JSON
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {/* Statistics */}
                      <div className="mb-3">
                        <Row>
                          <Col md={4}>
                            <strong>Source 1:</strong>{" "}
                            {comparisonResult.source1Info || "N/A"}
                          </Col>
                          <Col md={4}>
                            <strong>Source 2:</strong>{" "}
                            {comparisonResult.source2Info || "N/A"}
                          </Col>
                          <Col md={4}>
                            <strong>Different Fields:</strong>{" "}
                            {comparisonResult.differentFieldsCount || 0}
                          </Col>
                        </Row>
                      </div>

                      {/* JSON View */}
                      {comparisonResult.differences &&
                      Object.keys(comparisonResult.differences).length > 0 ? (
                        <div className="json-viewer">
                          <pre className="json-content">
                            {JSON.stringify(
                              {
                                source1Info: comparisonResult.source1Info,
                                source2Info: comparisonResult.source2Info,
                                totalFieldsCompared:
                                  comparisonResult.totalFieldsCompared,
                                differentFieldsCount:
                                  comparisonResult.differentFieldsCount,
                                differences: comparisonResult.differences,
                              },
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      ) : (
                        <Alert variant="info">
                          No differences found! The records are identical.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default MarketDataComparison;

